import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiEdit2, FiX, FiCheck, FiPlus, FiTrash2 } from 'react-icons/fi';

const DEFAULT_FOOTER = {
  companyName: 'BRAND LOGO',
  tagline: 'Building amazing experiences for everyone.',
  copyright: '© 2025 Your Company. All rights reserved.',
  backgroundColor: '#111827',
  textColor: '#9ca3af',
  accentColor: '#2dd4bf',
  headingColor: '#ffffff',
  quickLinksTitle: 'Quick Links',
  contactTitle: 'Get In Touch',
  contactDescription: "Have questions? We'd love to hear from you.",
  contactButtonText: 'Contact Us',
  contactButtonBg: '#2dd4bf',
  contactButtonTextColor: '#ffffff',
  footerBottomText: 'Built with',
  links: [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ],
};

export { DEFAULT_FOOTER };

const FooterEditor = ({ footerData, onUpdate, onClose }) => {
  const [form, setForm] = useState({ ...DEFAULT_FOOTER, ...footerData });

  const updateLink = (i, field, value) => {
    const links = [...form.links];
    links[i] = { ...links[i], [field]: value };
    setForm({ ...form, links });
  };

  const addLink = () => setForm({ ...form, links: [...form.links, { label: 'New Link', href: '/' }] });
  const removeLink = (i) => setForm({ ...form, links: form.links.filter((_, idx) => idx !== i) });

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden"
      >
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-teal-100 rounded-2xl">
              <SafeIcon icon={FiEdit2} className="w-5 h-5 text-teal-600" />
            </div>
            <h3 className="font-black text-gray-900 text-xl uppercase tracking-tight">Edit Footer</h3>
          </div>
          <button onClick={onClose} className="p-2.5 hover:bg-white rounded-xl text-gray-400 hover:text-gray-600 transition-all">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
          
          {/* Company Info Section */}
          <div className="bg-gray-50 rounded-2xl p-5 space-y-4 border border-gray-100">
            <h4 className="text-xs font-black text-gray-700 uppercase tracking-widest flex items-center space-x-2">
              <div className="w-1 h-4 bg-teal-500 rounded-full"></div>
              <span>Company Information</span>
            </h4>
            
            {[
              { label: 'Company Name', key: 'companyName', placeholder: 'Your Company Name' },
              { label: 'Tagline', key: 'tagline', placeholder: 'Your company tagline' },
              { label: 'Copyright Text', key: 'copyright', placeholder: '© 2025 Your Company' },
            ].map(({ label, key, placeholder }) => (
              <div key={key}>
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-2">{label}</label>
                <input
                  type="text"
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  placeholder={placeholder}
                  className="w-full p-3 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 text-sm font-medium transition-all"
                />
              </div>
            ))}
          </div>

          {/* Color Scheme Section */}
          <div className="bg-gray-50 rounded-2xl p-5 space-y-4 border border-gray-100">
            <h4 className="text-xs font-black text-gray-700 uppercase tracking-widest flex items-center space-x-2">
              <div className="w-1 h-4 bg-purple-500 rounded-full"></div>
              <span>Color Scheme</span>
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Background Color', key: 'backgroundColor' },
                { label: 'Text Color', key: 'textColor' },
                { label: 'Heading Color', key: 'headingColor' },
                { label: 'Accent Color', key: 'accentColor' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-2">{label}</label>
                  <div className="flex items-center space-x-3 bg-white border border-gray-200 rounded-xl p-2">
                    <input 
                      type="color" 
                      value={form[key]} 
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })} 
                      className="w-10 h-10 rounded-lg cursor-pointer border-2 border-gray-100" 
                    />
                    <input
                      type="text"
                      value={form[key]}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      className="flex-1 text-xs text-gray-600 font-mono outline-none"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Links Section */}
          <div className="bg-gray-50 rounded-2xl p-5 space-y-4 border border-gray-100">
            <h4 className="text-xs font-black text-gray-700 uppercase tracking-widest flex items-center space-x-2">
              <div className="w-1 h-4 bg-blue-500 rounded-full"></div>
              <span>Quick Links</span>
            </h4>

            <div>
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-2">Section Title</label>
              <input
                type="text"
                value={form.quickLinksTitle}
                onChange={(e) => setForm({ ...form, quickLinksTitle: e.target.value })}
                placeholder="Quick Links"
                className="w-full p-3 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-400 text-sm font-medium"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Links</label>
                <button 
                  onClick={addLink} 
                  className="text-teal-500 hover:text-teal-600 flex items-center space-x-1.5 text-xs font-black px-3 py-1.5 bg-teal-50 hover:bg-teal-100 rounded-lg transition-all"
                >
                  <SafeIcon icon={FiPlus} className="w-3.5 h-3.5" />
                  <span>Add Link</span>
                </button>
              </div>
              <div className="space-y-2.5">
                {form.links.map((link, i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <input 
                      type="text" 
                      value={link.label} 
                      onChange={(e) => updateLink(i, 'label', e.target.value)} 
                      placeholder="Label" 
                      className="flex-1 p-2.5 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-400 font-medium" 
                    />
                    <input 
                      type="text" 
                      value={link.href} 
                      onChange={(e) => updateLink(i, 'href', e.target.value)} 
                      placeholder="/path" 
                      className="flex-1 p-2.5 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-400 font-medium" 
                    />
                    <button 
                      onClick={() => removeLink(i)} 
                      className="p-2.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gray-50 rounded-2xl p-5 space-y-4 border border-gray-100">
            <h4 className="text-xs font-black text-gray-700 uppercase tracking-widest flex items-center space-x-2">
              <div className="w-1 h-4 bg-green-500 rounded-full"></div>
              <span>Contact Section</span>
            </h4>

            {[
              { label: 'Section Title', key: 'contactTitle', placeholder: 'Get In Touch' },
              { label: 'Description', key: 'contactDescription', placeholder: 'Have questions?' },
              { label: 'Button Text', key: 'contactButtonText', placeholder: 'Contact Us' },
            ].map(({ label, key, placeholder }) => (
              <div key={key}>
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-2">{label}</label>
                <input
                  type="text"
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  placeholder={placeholder}
                  className="w-full p-3 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-400 text-sm font-medium"
                />
              </div>
            ))}

            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Button Background', key: 'contactButtonBg' },
                { label: 'Button Text Color', key: 'contactButtonTextColor' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-2">{label}</label>
                  <div className="flex items-center space-x-3 bg-white border border-gray-200 rounded-xl p-2">
                    <input 
                      type="color" 
                      value={form[key] || '#2dd4bf'} 
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })} 
                      className="w-10 h-10 rounded-lg cursor-pointer border-2 border-gray-100" 
                    />
                    <input
                      type="text"
                      value={form[key] || '#2dd4bf'}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      className="flex-1 text-xs text-gray-600 font-mono outline-none"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Bottom Section */}
          <div className="bg-gray-50 rounded-2xl p-5 space-y-4 border border-gray-100">
            <h4 className="text-xs font-black text-gray-700 uppercase tracking-widest flex items-center space-x-2">
              <div className="w-1 h-4 bg-orange-500 rounded-full"></div>
              <span>Footer Bottom</span>
            </h4>

            <div>
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest block mb-2">Bottom Text (before heart icon)</label>
              <input
                type="text"
                value={form.footerBottomText}
                onChange={(e) => setForm({ ...form, footerBottomText: e.target.value })}
                placeholder="Built with"
                className="w-full p-3 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-400 text-sm font-medium"
              />
            </div>
          </div>

        </div>

        <div className="p-6 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 flex space-x-3">
          <button 
            onClick={onClose} 
            className="flex-1 py-3.5 font-bold text-gray-600 hover:bg-white rounded-xl transition-all text-sm border border-gray-200"
          >
            Cancel
          </button>
          <button 
            onClick={() => { onUpdate(form); onClose(); }} 
            className="flex-[2] py-3.5 bg-teal-500 text-white font-black rounded-xl hover:bg-teal-600 transition-all text-sm flex items-center justify-center space-x-2 shadow-lg shadow-teal-500/20"
          >
            <SafeIcon icon={FiCheck} className="w-4 h-4" />
            <span>Save Footer</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const GlobalFooter = ({ footerData = {}, onUpdate, isEditable = false, siteSlug, onNavClick }) => {
  const [showEditor, setShowEditor] = useState(false);
  const [hovered, setHovered] = useState(false);

  const data = { ...DEFAULT_FOOTER, ...footerData };

  const formatLink = (href) => {
    if (!href || href.startsWith('http') || href.startsWith('#')) return href;
    if (!siteSlug) return href;
    const path = href === '/' ? '' : (href.startsWith('/') ? href : `/${href}`);
    return `/${siteSlug}${path}`;
  };

  const handleClick = (e, href) => {
    if (isEditable) {
      e.preventDefault();
      return;
    }
    if (onNavClick) {
      onNavClick(e, href, 'page');
    }
  };

  return (
    <>
      <footer
        onMouseEnter={() => isEditable && setHovered(true)}
        onMouseLeave={() => isEditable && setHovered(false)}
        className="relative w-full"
        style={{ backgroundColor: data.backgroundColor }}
      >
        {isEditable && hovered && (
          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={() => setShowEditor(true)}
              className="flex items-center space-x-2 bg-teal-500 text-white text-xs font-black uppercase tracking-widest px-4 py-2.5 rounded-full shadow-xl hover:bg-teal-600 transition-all hover:scale-105"
            >
              <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" />
              <span>Edit Footer</span>
            </button>
          </div>
        )}

        <div className="max-w-6xl mx-auto px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
            
            {/* Company Info Column */}
            <div className="col-span-1">
              <div className="text-xl font-black tracking-tighter mb-3" style={{ color: data.headingColor }}>
                {data.companyName}
              </div>
              <p className="text-sm leading-relaxed" style={{ color: data.textColor }}>
                {data.tagline}
              </p>
            </div>

            {/* Quick Links Column */}
            <div>
              <h4 
                className="text-[10px] font-black uppercase tracking-widest mb-4" 
                style={{ color: data.accentColor }}
              >
                {data.quickLinksTitle}
              </h4>
              <ul className="space-y-2.5">
                {data.links.map((link, i) => (
                  <li key={i}>
                    <a
                      href={formatLink(link.href)}
                      onClick={(e) => handleClick(e, link.href)}
                      className="text-sm hover:opacity-80 transition-opacity"
                      style={{ color: data.textColor }}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Column */}
            <div>
              <h4 
                className="text-[10px] font-black uppercase tracking-widest mb-4" 
                style={{ color: data.accentColor }}
              >
                {data.contactTitle}
              </h4>
              <p className="text-sm leading-relaxed mb-4" style={{ color: data.textColor }}>
                {data.contactDescription}
              </p>
              <button
                className="px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all hover:scale-105 shadow-lg"
                style={{ 
                  backgroundColor: data.contactButtonBg || data.accentColor, 
                  color: data.contactButtonTextColor || '#ffffff' 
                }}
              >
                {data.contactButtonText}
              </button>
            </div>
          </div>

          {/* Footer Bottom */}
          <div 
            className="border-t pt-6 flex flex-col sm:flex-row items-center justify-between gap-4" 
            style={{ borderColor: 'rgba(255,255,255,0.08)' }}
          >
            <p className="text-xs" style={{ color: data.textColor }}>
              {data.copyright}
            </p>
            <div className="flex items-center space-x-1.5">
              <span className="text-xs" style={{ color: data.textColor }}>
                {data.footerBottomText}
              </span>
              <span className="text-sm font-black" style={{ color: data.accentColor }}>❤</span>
            </div>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {showEditor && (
          <FooterEditor footerData={data} onUpdate={onUpdate} onClose={() => setShowEditor(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default GlobalFooter;